/**
 * Utilitário de Rastreamento
 * Dispara eventos para o Google Tag Manager (dataLayer, GTM-MXRVJ58D) e, quando
 * presentes, para GA4 (gtag) e Facebook Pixel (fbq). A configuração das conversões
 * do Google Ads/GA4 é feita no painel do GTM, mapeando estes eventos.
 */

import { getAttributionParams } from './attribution';

declare global {
    interface Window {
        dataLayer?: Record<string, unknown>[];
        gtag?: (...args: unknown[]) => void;
        fbq?: (...args: unknown[]) => void;
    }
}

export const trackEvent = (eventName: string, params?: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;

    // Google Tag Manager — canal principal do site
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...params });

    // GA4 direto (caso um tag de configuração gtag esteja presente)
    if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
    }

    // Facebook Pixel
    if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', eventName, params);
    }
};

export const trackConversion = (unit: 'sp', method: 'whatsapp' | 'form' | 'phone' | 'email') => {
    trackEvent('conversion_click', {
        unit,
        method,
        location: window.location.pathname,
        // A origem (Google Ads / campanha) viaja AQUI, no evento — nunca dentro
        // da mensagem que o cliente envia. Ver comentário em attribution.ts.
        ...getAttributionParams(),
    });
};

/**
 * Clique num destino da página de links (/links).
 *
 * É a única medição que a página existe para produzir: qual oferta o público
 * da bio escolhe. Um evento só, com o destino como parâmetro — em vez de um
 * evento por botão, que multiplicaria trabalho no GTM sem ganhar nada.
 *
 * ⚠ Evento NOVO: só chega ao GA4 depois de criado o gatilho e a tag no GTM,
 * e o parâmetro `destino` precisa de uma variável de camada de dados própria.
 * Parâmetro que não é mapeado explicitamente morre no dataLayer.
 */
export const trackBioLink = (destino: string) => {
    trackEvent('bio_link_click', {
        destino,
        location: window.location.pathname,
        ...getAttributionParams(),
    });
};

/**
 * Pageview de SPA. Como o site é single-page (react-router), a Tag do Google só
 * conta o carregamento inicial; cada troca de rota precisa empurrar este evento
 * pro dataLayer para o GTM disparar um page_view no GA4.
 * Evento no dataLayer: `spa_pageview`.
 */
export const trackPageview = () => {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'spa_pageview',
        page_path: window.location.pathname + window.location.search,
        page_location: window.location.href,
        page_title: document.title,
    });
};
